import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Send,
  RotateCcw,
  Clock,
  Calendar,
  TrendingUp,
  Package,
} from 'lucide-react';
import { PageHeader } from '../ui/PageHeader.js';
import { SectionLabel } from '../ui/index.js';
import { StatusPill } from '../ui/settings.js';
import { fetchJson, me } from '../api.js';

/**
 * Emails transactionnels (client request 2026-10-25 & 2026-10-26):
 * - Provider switch (Mailgun / SES) + credentials sur la page (jamais d'env Coolify).
 * - Fréquences configurables : Temps réel (5m), Quotidien ruptures (06h45),
 *   Rapport quotidien (07h00) et Rapport hebdomadaire (Lundi 07h15).
 * - Tests d'envoi en direct avec prévisualisation des gabarits réels et traçabilité des auteurs.
 */

interface EmailConfigResponse {
  provider: 'none' | 'mailgun' | 'ses';
  from: string;
  notifyPriceChanges: boolean;
  notifyStockChanges?: boolean;
  notifyMarketPriceChanges?: boolean;
  notifyOutOfStock: boolean;
  dailyDigest: boolean;
  weeklyDigest: boolean;
  mailgunDomain: string;
  mailgunApiKeySet: boolean;
  mailgunApiKeyPreview: string | null;
  mailgunRegion: 'eu' | 'us';
  sesAccessKeyId: string;
  sesSecretAccessKeySet: boolean;
  sesSecretAccessKeyPreview: string | null;
  sesRegion: string;
  active: boolean;
}

interface Banner {
  ok: boolean;
  text: string;
}

export function SettingsEmailPage(): React.ReactElement {
  const meQuery = useQuery({ queryKey: ['me'], queryFn: me });
  const queryClient = useQueryClient();
  const configQuery = useQuery({
    queryKey: ['email-config'],
    queryFn: () => fetchJson<EmailConfigResponse>('/api/v1/email/config'),
  });

  const [provider, setProvider] = useState<'none' | 'mailgun' | 'ses'>('none');
  const [from, setFrom] = useState('');
  const [notifyPriceChanges, setNotifyPriceChanges] = useState(true);
  const [notifyStockChanges, setNotifyStockChanges] = useState(true);
  const [notifyMarketPriceChanges, setNotifyMarketPriceChanges] = useState(true);
  const [notifyOutOfStock, setNotifyOutOfStock] = useState(true);
  const [dailyDigest, setDailyDigest] = useState(true);
  const [weeklyDigest, setWeeklyDigest] = useState(true);
  const [mailgunDomain, setMailgunDomain] = useState('');
  const [mailgunApiKey, setMailgunApiKey] = useState('');
  const [mailgunRegion, setMailgunRegion] = useState<'eu' | 'us'>('eu');
  const [sesAccessKeyId, setSesAccessKeyId] = useState('');
  const [sesSecretAccessKey, setSesSecretAccessKey] = useState('');
  const [sesRegion, setSesRegion] = useState('eu-west-3');
  const [testTo, setTestTo] = useState('');
  const [banner, setBanner] = useState<Banner | null>(null);

  useEffect(() => {
    const cfg = configQuery.data;
    if (cfg === undefined) return;
    setProvider(cfg.provider);
    setFrom(cfg.from);
    setNotifyPriceChanges(cfg.notifyPriceChanges);
    setNotifyStockChanges(cfg.notifyStockChanges ?? true);
    setNotifyMarketPriceChanges(cfg.notifyMarketPriceChanges ?? true);
    setNotifyOutOfStock(cfg.notifyOutOfStock);
    setDailyDigest(cfg.dailyDigest);
    setWeeklyDigest(cfg.weeklyDigest ?? true);
    setMailgunDomain(cfg.mailgunDomain);
    setMailgunRegion(cfg.mailgunRegion);
    setSesAccessKeyId(cfg.sesAccessKeyId);
    setSesRegion(cfg.sesRegion);
  }, [configQuery.data]);

  useEffect(() => {
    if (!testTo && meQuery.data?.email) {
      setTestTo(meQuery.data.email);
    }
  }, [meQuery.data, testTo]);

  const saveMutation = useMutation({
    mutationFn: () =>
      fetchJson<{ status: string; provider: string; active: boolean }>('/api/v1/email/config', {
        method: 'PUT',
        body: JSON.stringify({
          provider,
          from,
          notifyPriceChanges,
          notifyStockChanges,
          notifyMarketPriceChanges,
          notifyOutOfStock,
          dailyDigest,
          weeklyDigest,
          mailgunDomain,
          mailgunApiKey,
          mailgunRegion,
          sesAccessKeyId,
          sesSecretAccessKey,
          sesRegion,
        }),
      }),
    onSuccess: (res) => {
      setMailgunApiKey('');
      setSesSecretAccessKey('');
      setBanner({
        ok: true,
        text: res.active
          ? 'Configuration enregistree avec succes — notifications email actives.'
          : 'Configuration enregistree (fournisseur inactif : identifiants incomplets).',
      });
      void queryClient.invalidateQueries({ queryKey: ['email-config'] });
    },
    onError: (err: Error) => {
      setBanner({ ok: false, text: `Echec de l'enregistrement : ${err.message}` });
    },
  });

  const testMutation = useMutation({
    mutationFn: (kind: 'test' | 'price_alerts' | 'stock_report' | 'daily_digest' | 'weekly_report' | 'out_of_stock' | 'market_price_alerts') =>
      fetchJson<{ ok: boolean; provider: string; messageId: string | null; kind?: string; subject?: string; detail?: string }>(
        '/api/v1/email/test',
        { method: 'POST', body: JSON.stringify({ to: testTo, kind }) },
      ),
    onSuccess: (res) => {
      if (!res.ok) {
        setBanner({
          ok: false,
          text: `Échec de l'envoi : ${res.detail ?? 'Erreur signalée par le fournisseur email'}.`,
        });
        return;
      }
      setBanner({
        ok: true,
        text: `Email de test envoyé avec succès via ${res.provider} à ${testTo} (${res.subject ?? res.kind ?? 'Test'}).`,
      });
    },
    onError: (err: Error) => {
      setBanner({ ok: false, text: `Échec de l'envoi du test : ${err.message}` });
    },
  });

  const cfg = configQuery.data;
  const inputClass =
    'w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-ink outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 transition-all';
  const labelClass = 'block text-xs font-semibold text-ink mb-1';

  return (
    <div className="space-y-5 pb-12 max-w-5xl">
      <PageHeader
        title="Emails Transactionnels & Notifications"
        hint="Notifications en direct (variations de prix avec auteurs), alertes de ruptures et rapports d'activité quotidien et hebdomadaire aux administrateurs."
        badge={
          <StatusPill tone={configQuery.isLoading ? 'info' : configQuery.isError ? 'ko' : cfg?.active ? 'ok' : 'idle'}>
            {configQuery.isLoading
              ? 'Chargement…'
              : configQuery.isError
              ? "Erreur d'accès"
              : cfg?.active
              ? `Actif — ${cfg.provider === 'mailgun' ? 'Mailgun' : 'Amazon SES'}`
              : 'Désactivé'}
          </StatusPill>
        }
      />

      {configQuery.isError && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-bold">Accès non autorisé ou session expirée</p>
          <p className="mt-1 text-xs text-amber-800">
            La configuration des emails transactionnels et l'envoi de tests sont réservés aux administrateurs.
            Si vous êtes administrateur, veuillez vous reconnecter pour actualiser vos droits.
          </p>
        </div>
      )}

      {banner !== null && (
        <div
          className={`rounded-xl border px-4 py-3 text-sm flex items-center justify-between ${
            banner.ok
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-red-200 bg-red-50 text-red-900'
          }`}
        >
          <span>{banner.text}</span>
          <button
            type="button"
            className="text-xs font-bold underline ml-4 hover:opacity-80"
            onClick={() => setBanner(null)}
          >
            Fermer
          </button>
        </div>
      )}

      {/* 1. Fournisseur & Expéditeur */}
      <section className="rounded-xl border border-border bg-card p-5 shadow-2xs space-y-4">
        <SectionLabel>1. Fournisseur &amp; Identifiants de Connexion</SectionLabel>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Fournisseur actif</label>
            <select
              className={inputClass}
              value={provider}
              onChange={(e) => setProvider(e.target.value as 'none' | 'mailgun' | 'ses')}
            >
              <option value="none">Aucun (emails desactives)</option>
              <option value="mailgun">Mailgun (Recommande)</option>
              <option value="ses">Amazon SES</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Expediteur (From)</label>
            <input
              className={inputClass}
              placeholder="MaPara <hello@1405.tn>"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
        </div>

        {provider === 'mailgun' && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 pt-2 border-t border-border/50">
            <div>
              <label className={labelClass}>Domaine Mailgun</label>
              <input
                className={inputClass}
                placeholder="mg.1405.tn"
                value={mailgunDomain}
                onChange={(e) => setMailgunDomain(e.target.value)}
              />
            </div>
            <div>
              <label className={labelClass}>
                Cle API {cfg?.mailgunApiKeySet ? `(enregistree : ${cfg.mailgunApiKeyPreview ?? '••••'})` : ''}
              </label>
              <input
                className={inputClass}
                type="password"
                placeholder={cfg?.mailgunApiKeySet ? 'Laisser vide pour conserver' : 'key-…'}
                value={mailgunApiKey}
                onChange={(e) => setMailgunApiKey(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div>
              <label className={labelClass}>Region Mailgun</label>
              <select
                className={inputClass}
                value={mailgunRegion}
                onChange={(e) => setMailgunRegion(e.target.value as 'eu' | 'us')}
              >
                <option value="eu">Europe (api.eu.mailgun.net)</option>
                <option value="us">USA (api.mailgun.net)</option>
              </select>
            </div>
          </div>
        )}

        {provider === 'ses' && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 pt-2 border-t border-border/50">
            <div>
              <label className={labelClass}>Access Key ID (IAM)</label>
              <input
                className={inputClass}
                placeholder="AKIA…"
                value={sesAccessKeyId}
                onChange={(e) => setSesAccessKeyId(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div>
              <label className={labelClass}>
                Secret Access Key {cfg?.sesSecretAccessKeySet ? `(enregistre : ${cfg.sesSecretAccessKeyPreview ?? '••••'})` : ''}
              </label>
              <input
                className={inputClass}
                type="password"
                placeholder={cfg?.sesSecretAccessKeySet ? 'Laisser vide pour conserver' : 'cle secrete'}
                value={sesSecretAccessKey}
                onChange={(e) => setSesSecretAccessKey(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div>
              <label className={labelClass}>Region SES</label>
              <input
                className={inputClass}
                placeholder="eu-west-3"
                value={sesRegion}
                onChange={(e) => setSesRegion(e.target.value)}
              />
            </div>
          </div>
        )}
      </section>

      {/* 2. Fréquences et Rapports */}
      <section className="rounded-xl border border-border bg-card p-5 shadow-2xs space-y-3">
        <SectionLabel>2. Frequences &amp; Types de Notifications</SectionLabel>
        <p className="text-xs text-ink-muted">
          Activez les alertes et rapports d'activite. Les destinataires sont automatiquement tous les comptes administrateurs actifs du systeme.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
          {/* Real-time price changes */}
          <label className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-slate-50 transition-colors cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-border text-emerald-600 focus:ring-emerald-500"
              checked={notifyPriceChanges}
              onChange={(e) => setNotifyPriceChanges(e.target.checked)}
            />
            <div>
              <div className="text-sm font-bold text-ink flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-emerald-600" />
                Alertes Changements de Prix Woo (Temps réel)
              </div>
              <div className="text-xs text-ink-muted mt-0.5">
                Vérifie toutes les 5 min. Envoie un digest immédiat avec tableau détaillé et l'auteur de chaque modification (App MaPara vs WooCommerce direct).
              </div>
            </div>
          </label>

          {/* Real-time stock changes */}
          <label className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-slate-50 transition-colors cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-border text-emerald-600 focus:ring-emerald-500"
              checked={notifyStockChanges}
              onChange={(e) => setNotifyStockChanges(e.target.checked)}
            />
            <div>
              <div className="text-sm font-bold text-ink flex items-center gap-1.5">
                <Package className="h-4 w-4 text-emerald-600" />
                Alertes Mouvements de Stock Woo (Temps réel)
              </div>
              <div className="text-xs text-ink-muted mt-0.5">
                Vérifie toutes les 5 min (et envoi immédiat sur webhook). Alerte instantanée pour tout passage en stock ou rupture avec auteur et quantités.
              </div>
            </div>
          </label>

          {/* Market Price Changes (Depots & Competitors) */}
          <label className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-slate-50 transition-colors cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-border text-amber-600 focus:ring-amber-500"
              checked={notifyMarketPriceChanges}
              onChange={(e) => setNotifyMarketPriceChanges(e.target.checked)}
            />
            <div>
              <div className="text-sm font-bold text-ink flex items-center gap-1.5">
                <TrendingUp className="h-4 w-4 text-amber-600" />
                Alertes Prix Dépôts &amp; Concurrents (Veille Marché)
              </div>
              <div className="text-xs text-ink-muted mt-0.5">
                Vérifie toutes les 15 min. Détecte les variations tarifaires chez les grossistes (dépôts B2B) et concurrents (baisses et hausses de prix).
              </div>
            </div>
          </label>

          {/* Daily Out of stock */}
          <label className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-slate-50 transition-colors cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-border text-emerald-600 focus:ring-emerald-500"
              checked={notifyOutOfStock}
              onChange={(e) => setNotifyOutOfStock(e.target.checked)}
            />
            <div>
              <div className="text-sm font-bold text-ink flex items-center gap-1.5">
                <Package className="h-4 w-4 text-rose-600" />
                Alertes Nouvelles Ruptures (06h45)
              </div>
              <div className="text-xs text-ink-muted mt-0.5">
                Envoye tous les matins apres le scan miroir du catalogue. Identifie les produits fraichement tombes en rupture.
              </div>
            </div>
          </label>

          {/* Daily Digest */}
          <label className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-slate-50 transition-colors cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-border text-emerald-600 focus:ring-emerald-500"
              checked={dailyDigest}
              onChange={(e) => setDailyDigest(e.target.checked)}
            />
            <div>
              <div className="text-sm font-bold text-ink flex items-center gap-1.5">
                <TrendingUp className="h-4 w-4 text-blue-600" />
                Rapport Quotidien (07h00)
              </div>
              <div className="text-xs text-ink-muted mt-0.5">
                Bilan des dernieres 24h : commandes WooCommerce, mouvements tarifaires, etat des ruptures et file de revision GTIN.
              </div>
            </div>
          </label>

          {/* Weekly Report */}
          <label className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-slate-50 transition-colors cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-border text-emerald-600 focus:ring-emerald-500"
              checked={weeklyDigest}
              onChange={(e) => setWeeklyDigest(e.target.checked)}
            />
            <div>
              <div className="text-sm font-bold text-ink flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-indigo-600" />
                Rapport Hebdomadaire (Lundi 07h15)
              </div>
              <div className="text-xs text-ink-muted mt-0.5">
                Synthese complete des 7 derniers jours : volume de commandes, colis livres, repartition auteurs App vs Woo et top variations de prix.
              </div>
            </div>
          </label>
        </div>
      </section>

      {/* Bouton Sauvegarder */}
      <div className="flex justify-end">
        <button
          type="button"
          className="rounded-lg bg-emerald-600 px-6 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50 transition-all flex items-center gap-2"
          disabled={saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          {saveMutation.isPending ? 'Enregistrement en cours…' : 'Enregistrer la configuration email'}
        </button>
      </div>

      {/* 3. Banc d'Essai & Prévisualisation des Emails */}
      <section className="rounded-xl border border-border bg-card p-5 shadow-2xs space-y-4">
        <SectionLabel>3. Tests d'Envoi &amp; Validation des Gabarits</SectionLabel>
        <p className="text-xs text-ink-muted">
          Envoyez des apercus reels de chaque type d'email a l'adresse de votre choix pour valider le rendu graphique et l'affichage des auteurs.
        </p>

        <div className="flex flex-col sm:flex-row items-end gap-3 max-w-xl">
          <div className="w-full">
            <label className={labelClass}>Adresse email destinataire du test</label>
            <input
              className={inputClass}
              placeholder="votre-email@1405.tn"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
            />
            {testTo.trim() === '' && (
              <p className="text-[11px] text-amber-600 mt-1">
                Saisissez une adresse email pour activer les boutons de test d'envoi.
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 pt-2">
          <button
            type="button"
            className="flex items-center justify-center gap-2 rounded-lg border border-border bg-slate-50 px-3.5 py-2.5 text-xs font-bold text-slate-800 hover:bg-slate-100 hover:border-slate-300 disabled:opacity-50 transition-all"
            disabled={testMutation.isPending || testTo.trim() === ''}
            onClick={() => testMutation.mutate('test')}
          >
            <Send className="h-3.5 w-3.5 text-slate-500" />
            Test de Connexion (Ping)
          </button>

          <button
            type="button"
            className="flex items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-xs font-bold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50 transition-all"
            disabled={testMutation.isPending || testTo.trim() === ''}
            onClick={() => testMutation.mutate('price_alerts')}
          >
            <Clock className="h-3.5 w-3.5 text-emerald-600" />
            Tester Alerte Prix Woo
          </button>

          <button
            type="button"
            className="flex items-center justify-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-3.5 py-2.5 text-xs font-bold text-teal-800 hover:bg-teal-100 disabled:opacity-50 transition-all"
            disabled={testMutation.isPending || testTo.trim() === ''}
            onClick={() => testMutation.mutate('stock_report')}
          >
            <Package className="h-3.5 w-3.5 text-teal-600" />
            Tester Alerte Stock Woo
          </button>

          <button
            type="button"
            className="flex items-center justify-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs font-bold text-amber-800 hover:bg-amber-100 disabled:opacity-50 transition-all"
            disabled={testMutation.isPending || testTo.trim() === ''}
            onClick={() => testMutation.mutate('market_price_alerts')}
          >
            <TrendingUp className="h-3.5 w-3.5 text-amber-600" />
            Tester Alerte Veille Marché
          </button>

          <button
            type="button"
            className="flex items-center justify-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-xs font-bold text-rose-800 hover:bg-rose-100 disabled:opacity-50 transition-all"
            disabled={testMutation.isPending || testTo.trim() === ''}
            onClick={() => testMutation.mutate('out_of_stock')}
          >
            <Package className="h-3.5 w-3.5 text-rose-600" />
            Tester Alerte Ruptures (Stock)
          </button>

          <button
            type="button"
            className="flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3.5 py-2.5 text-xs font-bold text-blue-800 hover:bg-blue-100 disabled:opacity-50 transition-all"
            disabled={testMutation.isPending || testTo.trim() === ''}
            onClick={() => testMutation.mutate('daily_digest')}
          >
            <TrendingUp className="h-3.5 w-3.5 text-blue-600" />
            Tester Rapport Quotidien (24h)
          </button>

          <button
            type="button"
            className="flex items-center justify-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3.5 py-2.5 text-xs font-bold text-indigo-800 hover:bg-indigo-100 disabled:opacity-50 transition-all"
            disabled={testMutation.isPending || testTo.trim() === ''}
            onClick={() => testMutation.mutate('weekly_report')}
          >
            <Calendar className="h-3.5 w-3.5 text-indigo-600" />
            Tester Rapport Hebdomadaire (7j)
          </button>
        </div>

        {testMutation.isPending && (
          <div className="text-xs text-ink-muted flex items-center gap-2 animate-pulse pt-1">
            <RotateCcw className="h-3 w-3 animate-spin" />
            Generation et envoi de l'email de test en cours…
          </div>
        )}
      </section>
    </div>
  );
}
